import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import get from 'lodash/get';
import BundleBadge from 'yoda-site-components/lib/components/ProductBadges/BundleBadge';
import { selectPreferences } from 'yoda-site-components/lib/selectors/ContextSelector';
import { ErrorBoundary } from 'yoda-site-components/lib/components/ErrorBoundary/ErrorBoundary';
import CouponExclusionBadge from 'yoda-site-components/lib/components/CouponExclusionBadge/CouponExclusionBadge';
import Icon from 'yoda-core-components/lib/components/Icon/Icon';
import PromotionMessage from 'yoda-core-components/lib/assets/svg/PromotionMessage.svg';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import FinalPriceAfterCoupon from './DeprecatedComponents/FPAC';

export function getDisplayFPAC(
    { isNetLevelPrice, promoTotal },
    price,
    isFpacForBundle,
    couponAmount,
    bundleLevelPricingDetails
) {
    return !!(
        couponAmount &&
        isFpacForBundle &&
        price &&
        price !== '$0' &&
        price !== `$${couponAmount}` &&
        (isNetLevelPrice ? promoTotal : true) &&
        (bundleLevelPricingDetails ? bundleLevelPricingDetails.couponInfo : true)
    );
}

export function formatAmount(stringCount) {
    return stringCount % Math.floor(stringCount)
        ? stringCount.toLocaleString('en', { minimumFractionDigits: 2 })
        : stringCount;
}

export function getCouponAmount({
    min = 0,
    max = 0,
    isNetLevelPrice = false,
    promoTotal = 0,
} = {}) {
    if (isNetLevelPrice) {
        return formatAmount(promoTotal);
    }

    if (min && max) {
        if (min === max) {
            return formatAmount(min);
        }

        return `${formatAmount(min)} - ${formatAmount(max)}`;
    }

    return null;
}

export function priceTag(_, expression) {
    const price = expression;

    if (price) {
        const priceHtml = price.split('$').join('$');
        return <span dangerouslySetInnerHTML={{ __html: priceHtml }} />; // eslint-disable-line react/no-danger
    }

    return price;
}

export function showBundlePriceLabel(priceLabel) {
    return priceLabel === 'each' ? <span className={dt(['pl-1'])}>each </span> : null;
}

export const renderMaxSavings = (maxSavings) => <BundleBadge text={maxSavings} themeConfig="" />;

export const promotionsBlock = (promotions) => (
    <p className={dt(['lg:ml-auto'])} data-automation-id="at-price-promotions-label">
        <span className={dt(['pr-[5px]'])}>
            <Icon
                iconType="svg"
                className={dt(['fill-brand-red'])}
                width="16px"
                height="16px"
                viewBox="0 2 40 40"
            >
                <PromotionMessage />
            </Icon>
        </span>
        <span
            className={dt([
                'font-semibold',
                'italic',
                'text-sm',
                'leading-[1.43]',
                'text-brand-red',
            ])}
        >
            {promotions}
        </span>
    </p>
);

export const renderSpecialOfferings = (
    pricingDetails,
    isDisplayFPAC,
    showPayOnly,
    couponAmount,
    bogoDisplay,
    isNetLevelPrice,
    deviceType,
    buyNowPayLaterEligibleLen
) => {
    const { couponInfo, promotions, isBundleLevelPrice } = pricingDetails;

    const alphaId = get(couponInfo, 'alphaId', '');
    const eligibilityMessage = get(couponInfo, 'eligibilityMessage', '');
    const borderStyle = isDisplayFPAC
        ? ['border-b', 'border-solid', 'border-gray-15', 'py-2']
        : [''];
    /* istanbul ignore next */
    if (promotions || isDisplayFPAC) {
        return (
            <div className={dt([...borderStyle])}>
                {isDisplayFPAC && (
                    <ErrorBoundary>
                        <FinalPriceAfterCoupon
                            couponAmount={couponAmount}
                            eligibilityMessage={eligibilityMessage}
                            alphaId={alphaId}
                            isBundleLevelPrice={isBundleLevelPrice}
                            isNetLevelPrice={isNetLevelPrice || pricingDetails.isNetLevelPrice}
                            showPayOnly={showPayOnly}
                            fromMiniPDP={false}
                            buyNowPayLaterEligibleLen={buyNowPayLaterEligibleLen}
                            couponNetLevelPrice={pricingDetails.isNetLevelPrice}
                        />
                    </ErrorBoundary>
                )}
            </div>
        );
    }

    return null;
};

export class Pricing extends Component {
    static propTypes = {
        // eslint-disable-next-line react/require-default-props
        pricingDetails: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
        bundleEnabled: PropTypes.bool,
        productType: PropTypes.string,
        showPayOnly: PropTypes.bool,
        featureFlags: PropTypes.objectOf(PropTypes.bool),
        isSameCoupons: PropTypes.bool,
        allItemsHaveCoupons: PropTypes.bool,
        bundleLevelPricingDetails: PropTypes.object,
        isFpacForBundle: PropTypes.bool,
        isNetLevelPrice: PropTypes.bool,
        deviceType: PropTypes.oneOfType([PropTypes.object]),
        preferences: PropTypes.oneOfType([PropTypes.object]),
        buyNowPayLaterEligibleLen: PropTypes.number,
        availableItemLength: PropTypes.bool,
        isAddtoCartPricing: PropTypes.bool,
    };

    static defaultProps = {
        // eslint-disable-next-line react/default-props-match-prop-types
        pricingDetails: {},
        productType: '',
        showPayOnly: false,
        featureFlags: {},
        isSameCoupons: false,
        allItemsHaveCoupons: false,
        bundleLevelPricingDetails: null,
        isFpacForBundle: false,
        isNetLevelPrice: false,
        deviceType: {},
        bundleEnabled: false,
        preferences: {},
        buyNowPayLaterEligibleLen: 0,
        availableItemLength: 0,
        isAddtoCartPricing: false,
    };

    static fontBoldBlack = ['font-bold', 'leading-7', 'text-black', 'text-xLg'];

    static saleClearanceStyle = [
        'font-semibold',
        'italic',
        'pl-1',
        'text-brand-red',
        'text-sm',
        'leading-5',
    ];

    render() {
        const {
            pricingDetails,
            showPayOnly,
            isSameCoupons,
            allItemsHaveCoupons,
            featureFlags,
            bundleLevelPricingDetails,
            isFpacForBundle,
            isNetLevelPrice,
            deviceType,
            bundleEnabled,
            preferences,
            buyNowPayLaterEligibleLen,
            availableItemLength,
            isAddtoCartPricing,
        } = this.props;

        const {
            couponInfo = {},
            mapPrice,
            defaults,
            original,
            isBundleLevelPrice,
            promoTotal,
            promotions,
            showBogo,
        } = pricingDetails;

        let bogoDisplay = true;
        if (featureFlags.enableNewBogoLogic) {
            bogoDisplay = showBogo;
        }
        const price = !mapPrice && ((defaults !== '$0' && defaults) || original);

        const { min, max } = get(couponInfo, 'amount', {});
        const amountData = {
            min,
            max,
            promoTotal,
            isNetLevelPrice: pricingDetails.isNetLevelPrice,
        };
        const couponAmount = getCouponAmount(amountData);

        const formattedPrice = price && price.substring(1);

        /* istanbul ignore next */
        const isDisplayFPAC =
            getDisplayFPAC(
                pricingDetails,
                price,
                isFpacForBundle,
                couponAmount,
                bundleLevelPricingDetails
            ) &&
            (isBundleLevelPrice ? isSameCoupons && allItemsHaveCoupons : true) &&
            couponInfo;
        /* istanbul ignore next */
        const isSaleSummaryEnabled = () =>
            price !== original &&
            pricingDetails.originalType &&
            pricingDetails.original &&
            pricingDetails.original !== '$0';
        const renderCouponExclusion = !!(
            get(featureFlags, 'displayCouponExclusions', true) &&
            pricingDetails?.couponExclusionMessage
        );
        const bottomBorderClass =
            renderCouponExclusion || !price || price === '$0'
                ? ['']
                : ['border-b', 'border-solid', 'border-gray-15'];
        return (
            <div>
                <div>
                    {pricingDetails.maxSavings && renderMaxSavings(pricingDetails.maxSavings)}
                </div>
                <div
                    className={dt([
                        'flex',
                        'items-baseline',
                        ...bottomBorderClass,
                        renderCouponExclusion ? '' : 'pb-2',
                        // isBundleLevelPrice ? '' : 'pt-2',
                        'smMd:flex-col',
                        isAddtoCartPricing ? 'smOnly:!pt-6' : '',
                    ])}
                >
                    <p data-automation-id={`${pricingDetails.type}-price-info`}>
                        {get(featureFlags, 'enableMarketingLabel', true) && (
                            <p
                                className={dt([
                                    'font-semibold',
                                    'italic',
                                    'text-sm',
                                    'leading-5',
                                    'text-brand-red',
                                ])}
                                data-automation-id="at-price-promotions-label"
                            >
                                {pricingDetails.marketingLabel}
                            </p>
                        )}
                        {pricingDetails.mapPrice && (
                            <p
                                className={dt([
                                    'font-bold',
                                    'leading-7',
                                    'pr-0.5',
                                    'text-black',
                                    'text-xLg',
                                ])}
                                data-automation-id="at-price-map-price-label"
                            >
                                {pricingDetails.mapPrice}
                            </p>
                        )}
                        {price && price !== '$0' && (
                            <span
                                data-automation-id="at-price-value"
                                className={dt([...Pricing.fontBoldBlack])}
                            >{priceTag`${price}`}</span>
                        )}
                        {showBundlePriceLabel(pricingDetails.priceLabel)}
                        {pricingDetails.type && pricingDetails.type !== 'original' && (
                            <span
                                data-automation-id="at-price-label"
                                className={dt([...Pricing.saleClearanceStyle])}
                            >
                                {pricingDetails.type}
                            </span>
                        )}
                        {isSaleSummaryEnabled() && (
                            <span
                                className={dt(['italic', 'leading-5', 'text-sm', 'text-gray-60'])}
                            >
                                {pricingDetails.percentOff && (
                                    <span
                                        data-automation-id="price-percent-off"
                                        className={dt(['pl-4'])}
                                    >
                                        {pricingDetails.percentOff}
                                    </span>
                                )}
                                <span
                                    data-automation-id="price-original-price"
                                    className={dt(['pl-4', 'line-through'])}
                                >
                                    {pricingDetails.original}
                                </span>
                            </span>
                        )}
                    </p>
                    {promotions && bogoDisplay && promotionsBlock(promotions)}
                </div>
                {renderSpecialOfferings(
                    pricingDetails,
                    isDisplayFPAC,
                    showPayOnly,
                    couponAmount,
                    bogoDisplay,
                    isNetLevelPrice,
                    deviceType,
                    buyNowPayLaterEligibleLen
                )}
                {renderCouponExclusion ? (
                    <CouponExclusionBadge
                        couponExclusionMessage={pricingDetails?.couponExclusionMessage}
                    />
                ) : null}
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    deviceType: state.context ? state.context.deviceType : {},
    featureFlags: get(state, 'context.featureFlags', {}),
    preferences: selectPreferences(state),
});

export default connect(mapStateToProps)(Pricing);
